/**
 * Message Bubble (Checkup assessment flow)
 *
 * NEWLINES (F34). Guide configs are authored with `\n` between paragraphs —
 * 47 of them across the five guides — and a plain `<p>` collapses every one of
 * them into a single space. "Your Household" and "Who is moving with you?" ran
 * together on screen as one sentence, which is how the production report read
 * "Seu Doméstico Quem está se mudando com você?".
 *
 * `whitespace-pre-line` preserves newlines and still collapses runs of spaces,
 * which is what guide copy wants. The hybrid renderer
 * (`ConversationRenderer.tsx`) already does this; the Companions use `Markdown`
 * and must NOT get `pre-line` on top, because that double-spaces every block.
 */

import { cn, TypingMessage } from '@tonnie/ui';
import { CheckCircle2 } from '@tonnie/assets';
import type { IMessage } from './types';
import { useLocale } from '@tonnie/locale';

interface IMessageBubbleProps {
  message: IMessage;
}

export function MessageBubble({ message }: IMessageBubbleProps) {
  const { formatTime } = useLocale();
  if (message.isCompletionMessage) {
    return (
      <div
        className="flex justify-center my-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
        data-testid="guide-message-completion"
      >
        <div className="max-w-md px-4 py-2.5 rounded-full bg-muted/60 border border-border/60 backdrop-blur-sm shadow-sm">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground font-medium text-center whitespace-pre-line">
              {message.isTyping ? (
                <TypingMessage content={message.content} speed={30} className="text-xs" />
              ) : (
                <span data-typing={false}>{message.content}</span>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex ${message.type === 'bot' ? 'justify-start' : 'justify-end'}`}
      data-testid={message.type === 'bot' ? 'guide-message-bot' : 'guide-message-user'}
    >
      <div
        className={cn(
          'max-w-xs lg:max-w-md px-4 py-3 rounded-2xl',
          message.type === 'bot'
            ? 'bg-card text-card-foreground rounded-bl-none shadow-sm'
            : 'bg-primary text-primary-foreground rounded-br-none shadow-sm'
        )}
      >
        {message.isTyping && message.type === 'bot' ? (
          <TypingMessage content={message.content} speed={30} className="text-sm leading-relaxed" />
        ) : (
          // Settled copy says so the way a typing message does (`data-typing`),
          // so "the message has finished arriving" reads the same on both paths.
          <p
            className={cn(
              'text-sm leading-relaxed whitespace-pre-line',
              message.type === 'user' && 'text-primary-foreground'
            )}
            data-typing={false}
          >
            {message.content}
          </p>
        )}
        <p
          className={cn(
            'text-xs mt-1',
            message.type === 'bot' ? 'text-muted-foreground' : 'text-primary-foreground/70'
          )}
        >
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
}
